'use client';

import {
  useState
} from 'react';

import type {
  PracticeLevel
} from '@repo/api-client/content-management-service';

import {
  LEVEL_CHOICES,
  LEVEL_FIXED_SENTENCE,
  lockChoices,
  type LevelLock
} from '@/app/lib/practice/level-flow';

import type {
  PracticePrimer
} from '@/app/lib/practice/types';

// Explanation levels (Brain elite-scholars/30-projects/practice-levels-design.md):
// the choice before a session, the way out of a level lock, and the
// Start-from-scratch primer. Named for the approach, never the learner.

const CARD = 'rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900';

const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(1,158,250)]';

const MAIN = `min-h-[44px] px-5 rounded-full text-sm font-semibold bg-[rgb(1,158,250)] text-white hover:bg-[rgb(0,140,225)] transition-colors motion-reduce:transition-none disabled:opacity-60 ${ FOCUS }`;

const SOFT = `min-h-[44px] px-4 rounded-full text-sm font-semibold border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:border-[rgba(1,158,250,0.5)] transition-colors motion-reduce:transition-none disabled:opacity-50 ${ FOCUS }`;

/** Server-rendered HTML with MathML from our own content; nothing a user typed. */
function MathHtml({
  html, className = '', as = 'div'
}: { html: string
  className?: string
  as?: 'div' | 'span' }) {

  const Tag = as;

  return (
    <Tag
      className={`practice-maths ${ className }`}
      dangerouslySetInnerHTML={{
        __html: html
      }}
    />
  );
}

/**
 * Pick how to work before the session starts. The level cannot change
 * inside a session, and the screen says so before the learner commits.
 */
export function LevelChooser({
  initial = 'guided', restarting, busy, onStart, onCancel
}: {
  initial?: PracticeLevel
  /** True when this replaces a session (Restart): the button says so. */
  restarting: boolean
  busy: boolean
  onStart: (level: PracticeLevel) => void
  /** Back to the session on the page; absent when there is none. */
  onCancel?: () => void
}) {

  const [ level, setLevel ] = useState<PracticeLevel>(initial);

  return (
    <section className={`${ CARD } p-5 space-y-4`} aria-labelledby="level-choice-title">
      <div>
        <h2 id="level-choice-title" className="text-base font-semibold text-[#111111] dark:text-white">
          How do you want to work?
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{LEVEL_FIXED_SENTENCE}</p>
      </div>

      <div role="radiogroup" aria-labelledby="level-choice-title" className="grid grid-cols-1 gap-2.5 min-[720px]:grid-cols-3">
        {LEVEL_CHOICES.map((choice) => {

          const selected = choice.level === level;

          return (
            <button
              key={choice.level}
              type="button"
              role="radio"
              aria-checked={selected}
              data-level={choice.level}
              onClick={() => setLevel(choice.level)}
              className={`min-h-[44px] min-w-0 text-left rounded-2xl border px-4 py-3.5 transition-colors motion-reduce:transition-none ${ FOCUS } ${ selected
                ? 'border-[rgb(1,158,250)] bg-sky-50 dark:bg-sky-950/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-[rgba(1,158,250,0.5)]' }`}
            >
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-[#111111] dark:text-white">{choice.title}</span>
                {choice.recommended && (
                  <span className="text-[0.65rem] uppercase tracking-wide font-semibold text-[rgb(0,113,190)] dark:text-sky-300 border border-sky-200 dark:border-sky-800 rounded-full px-1.5 py-0.5">
                    Recommended
                  </span>
                )}
              </span>
              <span className="block text-[13px] leading-snug text-gray-600 dark:text-gray-400 mt-1">{choice.description}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2.5">
        <button type="button" disabled={busy} onClick={() => onStart(level)} className={MAIN}>
          {busy ? 'Starting…' : (restarting ? 'Start again' : 'Start')}
        </button>
        {onCancel && (
          <button type="button" disabled={busy} onClick={onCancel} className={SOFT}>
            Cancel
          </button>
        )}
      </div>
    </section>
  );
}

/** A session at another level is open: continue it, or restart at the new level. */
export function LevelLockPanel({
  lock, busy, onContinue, onRestart
}: {
  lock: LevelLock
  busy: boolean
  onContinue: () => void
  onRestart: () => void
}) {

  const words = lockChoices(lock);

  return (
    <section className={`${ CARD } p-5 space-y-3`} role="alert">
      <p className="text-sm text-[#111111] dark:text-white">{words.message}</p>
      <div className="flex flex-wrap gap-2.5">
        <button type="button" disabled={busy} onClick={onContinue} className={MAIN}>
          {words.continueLabel}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {

            if (window.confirm(words.restartConfirm)) {

              onRestart();
            }
          }}
          className={SOFT}
        >
          {words.restartLabel}
        </button>
      </div>
    </section>
  );
}

function PrimerBody({
  primer
}: { primer: PracticePrimer }) {

  return (
    <div className="space-y-4 text-[#111111] dark:text-gray-100">
      <div className="space-y-3">
        {primer.paragraphsHtml.map((html, i) => (
          <MathHtml key={i} html={html} className="text-[15px] leading-relaxed min-w-0 overflow-x-auto overflow-y-hidden" />
        ))}
      </div>
      <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-4 space-y-3 min-w-0">
        <p className="text-sm font-semibold">Worked example</p>
        <MathHtml html={primer.example.promptHtml} className="text-[15px] leading-relaxed" />
        {primer.example.displayHtml && (
          <MathHtml html={primer.example.displayHtml} className="overflow-x-auto overflow-y-hidden whitespace-nowrap text-lg py-1" />
        )}
        <ol className="space-y-3">
          {primer.example.workedHtml.map((line, i) => (
            <li key={i} className="space-y-1 min-w-0">
              <span className="inline-block text-xs font-semibold text-[rgb(0,113,190)] dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 rounded-full px-2.5 py-0.5">
                {`Step ${ i + 1 }`}
              </span>
              <MathHtml html={line.sayHtml} className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300" />
              {line.mathHtml && (
                <MathHtml html={line.mathHtml} className="overflow-x-auto overflow-y-hidden whitespace-nowrap py-1 text-lg" />
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/** Start from scratch: the topic primer before question 1. */
export function PrimerCard({
  primer, onBegin
}: {
  primer: PracticePrimer
  onBegin: () => void
}) {

  return (
    <section className={`${ CARD } p-5 space-y-4 min-w-0`} aria-labelledby="primer-title" data-testid="practice-primer">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Before you start</p>
        <h2 id="primer-title" className="text-lg font-semibold text-[#111111] dark:text-white mt-1">{primer.title}</h2>
      </div>
      <PrimerBody primer={primer} />
      <button type="button" onClick={onBegin} className={MAIN}>
        Start question 1
      </button>
    </section>
  );
}

/** The primer again, folded away, above the questions. */
export function PrimerFold({
  primer
}: { primer: PracticePrimer }) {

  return (
    <details className={`${ CARD } px-5 py-3 mb-4 min-w-0`} data-testid="practice-primer-fold">
      <summary className={`min-h-[44px] flex items-center cursor-pointer text-sm font-semibold text-[rgb(0,113,190)] dark:text-sky-300 ${ FOCUS }`}>
        {`Topic primer: ${ primer.title }`}
      </summary>
      <div className="pt-2 pb-2">
        <PrimerBody primer={primer} />
      </div>
    </details>
  );
}
